import {
	ReactElement,
	useEffect,
	useMemo
} from "react";
import { ValidGlobalSearchItemPersistedModel } from "../../Persistence/validation";
import { GlobalSearchResultItemKind } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { SearchListItem } from "client/Features/Search/SearchListItem";
import { generateTrackingId } from "client/Misc/utils";
import { DocumentItemViewModel } from "client/Api/Documents/SharePoint/common";
import { useDocumentNavigationHandler } from "client/Features/Layout/TitleBar/GlobalSearch/Navigation/useDocumentNavigationHandler";
import { DocumentListItem } from "client/Features/Layout/TitleBar/GlobalSearch/Items/DocumentListItem";
import { TimeZone } from "client/DateAndTime/timezones";
import { convertDocumentResultToDocumentViewModel } from "../../Persistence/convertPersistedData";

export type DocumentItemViewProps = {
	item: ValidGlobalSearchItemPersistedModel<GlobalSearchResultItemKind.Document>;
	navigationIndex: number;
	index: number;
	onRemove: () => void;
	isMobile: boolean;
	timezone: TimeZone;
};

export const DocumentItemView = (props: DocumentItemViewProps): ReactElement | null => {
	const {
		item,
		navigationIndex,
		index,
		onRemove,
		isMobile,
		timezone
	} = props;

	const documentConversion = useMemo(
		(): TryResult<DocumentItemViewModel> => convertDocumentResultToDocumentViewModel(item.document),
		[item.document]
	);

	const executeHandler: () => void = useDocumentNavigationHandler({
		document: documentConversion.wasSuccessful
			? documentConversion.result
			: "unresolved",
		recentlyOpenedItem: item
	});

	useEffect(
		(): void => {
			if (!documentConversion.wasSuccessful) {
				console.error("Unable to convert document result item to document view model.");
			}
		},
		[documentConversion.wasSuccessful]
	);

	if (!documentConversion.wasSuccessful) {
		return null;
	}

	return (
		<SearchListItem
			onExecute={executeHandler}
			trackingId={generateTrackingId("document-item", `|${index}`)}
		>
			<DocumentListItem
				document={documentConversion.result}
				isMobile={isMobile}
				timezone={timezone}
				onRemove={onRemove}
				itemIndex={navigationIndex}
				tabIndex={-1}
				onExecute={executeHandler}
			/>
		</SearchListItem>
	);
};
DocumentItemView.displayName = "DocumentItemView";